import { useDisposable } from "@kosmod/graphics-3d";
import { Array2D, mod, useJeffect, useLogger } from "@kosmod/kit";
import { CanvasCoordinator, ChunkMap, ReadonlyChunkMap } from "@kosmod/stew";
import { Component, h, useEffect, useMemo, useRef, useState } from "@lukekaalim/act";
import { a3 } from "@lukekaalim/act-three";
import { CanvasTexture, MeshBasicMaterial, NearestFilter, PlaneGeometry, UVMapping, Vector2, Vector2Like } from "three";

export type SampleTerrainSystemProps<T> = {
  chunkMap: ReadonlyChunkMap<T>,
  coordinator: CanvasCoordinator,
  drawTile: (ctx: CanvasRenderingContext2D, x: number, y: number, value: T) => void,

  resolution?: Vector2Like,
}

/**
 * Sample the chunkmap at each section, drawing the result to
 * a texture
 */
export const SampleTerrainSystem = <T>({ chunkMap, drawTile, coordinator, resolution = { x: 3, y: 3 } }: SampleTerrainSystemProps<T>) => {
  const [chunks, setChunks] = useState([...chunkMap.chunks.keys()]);

  const logger = useLogger('SampleTerrainSystem')

  useJeffect(j => {
    setChunks([...chunkMap.chunks.keys()]);

    j.add(coordinator.updates.subscribe((chunks) => {
      const sampledChunks = chunks.filter(([key, action]) => key[0] === chunkMap.canvasId);

      if (sampledChunks.length > 0) {
        setChunks([...chunkMap.chunks.keys()]);
      }
    }))
  }, [chunkMap.chunks.keys, chunkMap.canvasId])

  return useMemo(() => {
    return chunks.map(([chunkX, chunkY]) => h(SampleTerrainChunk, { chunkX, chunkY, drawTile, chunkMap, coordinator, resolution }))
  }, [chunks])
}

type SampleTerrainChunkProps<T> = SampleTerrainSystemProps<T> & {
  chunkX: number,
  chunkY: number,
}

const SampleTerrainChunk = <T>({ chunkMap, chunkX, chunkY, drawTile, coordinator, resolution = { x: 3, y: 3 } }: SampleTerrainChunkProps<T>) => {
  const logger = useLogger('SampleTerrainSystem').createSubsystem('Chunk')

  const [drawCtx, canvas] = useRef(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 8 * resolution.x;
    canvas.height = 8 * resolution.y;
    const draw = canvas.getContext('2d')!;
    
    return [draw, canvas] as const;
  }).get();

  const texture = useDisposable(() => {
    const texture = new CanvasTexture(canvas);
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    return texture;
  }, [canvas]);

  useJeffect(j => {
    const draw = () => {
      drawCtx.fillStyle = 'black';
      drawCtx.fillRect(0, 0, 8 * resolution.x, 8 * resolution.y);

      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          drawTile(drawCtx, x * resolution.x, y * resolution.y, chunkMap.read(x + (chunkX * 8), y + (chunkY * 8)))
        }
      }

      texture.needsUpdate = true;
    };

    j.add(coordinator.updates.subscribe(c => {
      if (c.some(([chunk]) => chunk[0] === chunkMap.canvasId && chunk[1] === chunkX && chunk[2] === chunkY)) {
        draw();
      }
    }));

    draw();
  }, [drawCtx, coordinator, chunkX, chunkY, drawTile, texture])


  const material = useDisposable(() => {
    return new MeshBasicMaterial({ map: texture });
  }, [texture])

  logger.log({ chunkX, chunkY });

  return h(a3.mesh, {
    position: { x: (chunkX * 8) + 4, y: 0, z: (chunkY * 8) + 4 },
    geometry,
    material
  });
}

const geometry = new PlaneGeometry(8, 8, 1, 1)
  .rotateX(Math.PI * -0.5)